import { useMemo, useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import { cx } from '../../lib/cx';
import { logLevelClass, looksLikeLog, lowlight } from './highlighting';

/**
 * A rendered code block: highlighted source, tinted logs, and a copy button.
 *
 * Read-only — this is the VIEW half. The editors get their highlighting from
 * `CodeBlockLowlight` (see extensions.ts), which decorates the live document;
 * this renders the same stored text for everyone reading it afterwards. Both
 * go through the same `lowlight` instance, so a block cannot look one way
 * while being written and another once posted.
 *
 * Three paths, in order:
 *
 *  1. **A log.** Highlighted by level, not by syntax — there is no grammar for
 *     "a log" and pretending otherwise produces confident nonsense. See
 *     `logLevelClass`.
 *  2. **Code lowlight can place.** Highlighted properly.
 *  3. **Everything else.** Monospace, preserved exactly. Which is a perfectly
 *     good answer, and the one this falls back to whenever highlighting throws
 *     — a syntax highlighter crashing must never take a request page with it.
 */
export function CodeBlock({ text, language }: { text: string; language?: string | null }) {
  const [copied, setCopied] = useState(false);

  const body = useMemo((): ReactNode => {
    if (looksLikeLog(text)) return <LogLines text={text} />;

    try {
      // `typeof`, not `!= null`: the prop is `string | null | undefined` and the
      // codebase compares explicitly everywhere else rather than leaning on the
      // loose-equality nullish idiom.
      const tree =
        typeof language === 'string' && lowlight.registered(language)
          ? lowlight.highlight(language, text)
          : lowlight.highlightAuto(text);
      return toNodes(tree.children);
    } catch {
      // Unknown language, malformed input, a grammar that threw — the text is
      // what matters and it is already here.
      return text;
    }
  }, [text, language]);

  const copy = () => {
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="group relative my-1">
      <pre className="overflow-x-auto rounded-[8px] bg-surface-3 p-2.5 font-mono fs-12 leading-relaxed">
        <code>{body}</code>
      </pre>
      {/* A pasted log or stack trace is nearly always on its way somewhere
          else — a terminal, a search box, a ticket. */}
      <button
        type="button"
        onClick={copy}
        aria-label="Copy"
        className="absolute top-1.5 right-1.5 rounded-md bg-base/80 p-1 text-muted opacity-0 group-hover:opacity-100 hover:text-ink focus-visible:opacity-100"
      >
        {copied ? <Check size={13} className="text-success" /> : <Copy size={13} />}
      </button>
    </div>
  );
}

/** One span per line, tinted by level. Lines without a level are left alone. */
function LogLines({ text }: { text: string }) {
  return (
    <>
      {text.split('\n').map((line, index) => {
        const tone = logLevelClass(line);
        return (
          <span key={index} className={cx('block', tone ?? undefined)}>
            {line === '' ? ' ' : line}
          </span>
        );
      })}
    </>
  );
}

/**
 * lowlight returns hast (an HTML syntax tree), not React. This walks it.
 *
 * The class names are highlight.js's own (`hljs-keyword`, `hljs-string`, …),
 * styled once in index.css against the app's tokens rather than by importing
 * one of highlight.js's stylesheets — those ship their own palette and would
 * put a second, unrelated colour scheme into a product that has one.
 */
type HastNode = {
  type: string;
  value?: string;
  tagName?: string;
  properties?: { className?: unknown };
  children?: HastNode[];
};

function toNodes(nodes: readonly HastNode[]): ReactNode {
  return nodes.map((node, index) => {
    if (node.type === 'text') return node.value ?? '';
    const className = Array.isArray(node.properties?.className)
      ? (node.properties.className as string[]).join(' ')
      : undefined;
    return (
      <span key={index} className={className}>
        {toNodes(node.children ?? [])}
      </span>
    );
  });
}
